import React, { useEffect, useRef, useState } from "react";
import Isotope from "isotope-layout";
import ProjectDetailsModal from "./ProjectDetailsModal";

const Portfolio = () => {
  // init one ref to store the future isotope object
  const isotope = useRef();
  // store the filter keyword in a state
  const [filterKey, setFilterKey] = useState("*");
  const [imagesLoaded, setimagesLoaded] = useState(0);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState();
  const [isOpen, setIsOpen] = useState(false);

  const htmlElement = document.getElementsByTagName("html")[0];
  const isRtl = htmlElement.getAttribute("dir") === "rtl";

  const filters = {
    DETAILED: "Details",
    EXAMPLES: "Examples",
    YOUTUBE: "Youtube Videos",
  };

  const types = {
    IMAGE: "image",
    VIDEO: "video",
    DOCUMENT: "document",
    ILLUSTRATION: "Illustration",
  };

  const projectsData = [
    {
      title: "Keri — Kinetic REST Integrator",
      type: types.DOCUMENT,
      document: {
        projectInfo:
          "Open-source .NET SDK for the Epicor Kinetic REST API. Strongly-typed async service wrappers over Epicor business objects and BAQs, typed UD-table access with custom column mapping, Epicor Functions support, and a generic REST transport usable beyond Epicor. Transaction-aware error handling reports whether a failure occurred before or after the commit boundary — the distinction that determines whether a retry is safe. Companion packages handle Excel/CSV rendering and SMTP delivery. Released under Apache-2.0.",
        client: "Independent — open source",
        technologies:
          "C#, .NET Framework 4.6.1+ / .NET Standard 2.0 / .NET 8.0, Newtonsoft.Json, ClosedXML, MailKit",
        industry: "Developer Tooling / ERP",
        date: "Ongoing",
        url: {
          name: "View on GitHub",
          link: "https://github.com/mrjtgrant/KineticRESTIntegrator",
        },
        sliderImages: [],
      },
      thumbImage: "images/projects/RESTThumb.jpg",
      categories: [filters.DETAILED],
    },
    {
      title: "Storefront Inventory Integration",
      type: types.DOCUMENT,
      document: {
        projectInfo:
          "Integration of third-party inventory into an e-commerce storefront for indirect sales, bringing hard-to-find parts to an industry with a significant supply gap. The work was primarily an optimization problem: moving very large quantities of data and large files into the store as efficiently as possible, using asynchronous processing to keep system resources available throughout.",
        client: "Industrial supply distributor",
        technologies:
          "C# .NET, CSV, JSON, asynchronous threading for efficient resource use",
        industry: "eCommerce",
        date: "January 2024",
        sliderImages: [],
      },
      thumbImage: "images/projects/BigCommerce.jpg",
      categories: [filters.DETAILED],
    },
    {
      title: "RFID Demo",
      type: types.VIDEO,
      video: {
        vimeo: false,
        id: "wsrzLDmlKrM",
      },
      thumbImage: "images/projects/RFIDCollage.jpg",
      categories: [filters.YOUTUBE],
      category: "RFID",
    },
    {
      title: "Custom Shipping Software",
      type: types.DOCUMENT,
      document: {
        projectInfo:
          "Full shipment management software for shipment organization, automated pallet placement and labeling, PCID tracking, kit handling and order fulfillment tracking. Handles complex shipping campaigns and big-box retail shipping contracts, tracking progress at every stage.",
        client: "Building products manufacturer",
        technologies: "C#, .NET, MVC5, HTML5, CSS3, Bootstrap",
        industry: "ERP Systems",
        date: "July 2021",
        sliderImages: [
          "images/projects/CW_1.png",
          "images/projects/CW_2.png",
          "images/projects/CW_3.png",
        ],
      },
      thumbImage: "images/projects/CW_3.jpg",
      categories: [filters.DETAILED],
    },
    {
      title: "Where are my cats?",
      description: "RFID reads → Node/Express → MongoDB → React",
      tagStatus1: "upstairs",
      tagStatus2: "downstairs",
      type: types.IMAGE,
      thumbImage: "images/projects/MyApartment-1.jpg",
      categories: [filters.EXAMPLES],
      category: "RFID Integration",
    },
  ];

  // initialize an Isotope object with configs
  useEffect(() => {
    isotope.current = new Isotope(".portfolio-filter", {
      itemSelector: ".filter-item",
      layoutMode: "masonry",
      originLeft: !isRtl,
    });

    // cleanup
    return () => {
      isotope.current.destroy();
    };
  }, [isRtl]);

  // handling filter key change
  useEffect(() => {
    if (imagesLoaded) {
      filterKey === "*"
        ? isotope.current.arrange({ filter: `*` })
        : isotope.current.arrange({ filter: `.${filterKey}` });
    }
  }, [filterKey, imagesLoaded]);

  const handleFilterKeyChange = (key) => () => setFilterKey(key);

  const getKeyByValue = (value) => {
    return Object.keys(filters).find((key) => filters[key] === value);
  };

  const getFilterClasses = (categories) => {
    if (categories.length > 0) {
      let tempArray = [];
      categories.forEach((category, index) => {
        tempArray.push(getKeyByValue(category));
      });
      return tempArray.join(" ");
    }
  };

  return (
    <>
      <section id="portfolio" className={"section bg-light"}>
        <div className={"container"}>
          {/* Heading */}
          <p className="text-center mb-2 wow fadeInUp">
            <span className="bg-primary text-dark px-2">Portfolio</span>
          </p>
          <h2 className="text-10 fw-600 text-center mb-5 wow fadeInUp">
            Some of my most recent projects
          </h2>
          {/* Heading end*/}
          {/* Filter Menu */}
          <ul
            className={
              "portfolio-menu nav nav-tabs fw-600 justify-content-start justify-content-md-center border-bottom-0 mb-5 wow fadeInUp"
            }
          >
            <li className="nav-item">
              <button
                className={"nav-link " + (filterKey === "*" ? "active" : "")}
                onClick={handleFilterKeyChange("*")}
              >
                All
              </button>
            </li>
            {Object.keys(filters).map((oneKey, i) => (
              <li className="nav-item" key={i}>
                <button
                  className={
                    "nav-link " + (filterKey === oneKey ? "active" : "")
                  }
                  onClick={handleFilterKeyChange(oneKey)}
                >
                  {filters[oneKey]}
                </button>
              </li>
            ))}
          </ul>
          {/* Filter Menu end */}
          <div className="portfolio wow fadeInUp">
            <div className="row portfolio-filter filter-container g-4">
              {projectsData.length > 0 &&
                projectsData.map((project, index) => (
                  <div
                    className={
                      "col-sm-6 col-lg-4 filter-item " +
                      getFilterClasses(project.categories)
                    }
                    key={index}
                  >
                    <div className="portfolio-box">
                      <div className="portfolio-img">
                        <img
                          onLoad={() => {
                            setimagesLoaded(imagesLoaded + 1);
                          }}
                          className="img-fluid d-block portfolio-image"
                          src={project.thumbImage}
                          alt=""
                        />
                        <div
                          className="portfolio-overlay"
                          onClick={() => {
                            setSelectedProjectDetails(projectsData[index]);
                            setIsOpen(true);
                          }}
                        >
                          <button className="popup-ajax stretched-link border-0 p-0 ">
                            {" "}
                          </button>
                          <div className="portfolio-overlay-details">
                            <p className="text-primary text-8">
                              {project.type === types.DOCUMENT && (
                                <i className="fas fa-file-alt"></i>
                              )}
                              {project.type === types.IMAGE && (
                                <i className="fas fa-image"></i>
                              )}
                              {project.type === types.VIDEO && (
                                <i className="fas fa-video"></i>
                              )}
                              {project.type === types.ILLUSTRATION && (
                                <i className="fas fa-play"></i>
                              )}
                            </p>
                            <h5 className="text-white text-5">
                              {project?.title}
                            </h5>
                            <span className="text-light">
                              {project?.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>
      {/* Modal */}
      {isOpen && (
        <ProjectDetailsModal
          projectDetails={selectedProjectDetails}
          setIsOpen={setIsOpen}
        ></ProjectDetailsModal>
      )}
    </>
  );
};

export default Portfolio;
